import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import React from 'react';
import { SlotService } from '../../../../../utils/state/types';
import { getParticipantLabel } from '../../../../../utils/multiParticipantFlow';
import { getParticipantName } from '../../../../../utils/multiParticipantFlow/participants';
import { TagName } from '../../../../../utils/tagName.const';
import Text, { TextType } from '../../../Text/Text';
import { useFormComponentContext } from '../../../Hooks/useFormComponentContext';
import { SelectedAddons } from './SelectedAddons';
import { SingleSlotDetailsDataHook } from './dataHooks';
import { classes, st } from './ParticipantsDetails.st.css';

export type ParticipantsDetailsProps = {
  slotService: SlotService;
};

/**
 * Booking Details, multi-participant flow: one block per participant with "Participant N",
 * the name typed in their form, and their own add-ons. Rendered only with several
 * participants; a single participant keeps today's shared add-ons under "More details".
 */
export const ParticipantsDetails: React.FC<ParticipantsDetailsProps> = ({
  slotService,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const { participantFormValues } = useFormComponentContext();
  const participants = slotService.participants ?? [];

  if (participants.length <= 1) {
    return null;
  }

  return (
    <div
      className={st(classes.root, { isMobile })}
      data-hook={SingleSlotDetailsDataHook.PARTICIPANTS_DETAILS}
    >
      {participants.map((participant, index) => {
        const name = getParticipantName(participantFormValues?.[index]);
        return (
          <div
            key={participant.id}
            className={classes.participant}
            data-hook={`${SingleSlotDetailsDataHook.PARTICIPANT_DETAILS}-${index}`}
          >
            <div className={classes.header}>
              <Text
                type={TextType.Primary}
                tagName={TagName.Paragraph}
                data-hook={SingleSlotDetailsDataHook.PARTICIPANT_LABEL}
              >
                {getParticipantLabel(t, index + 1)}
              </Text>
              {name && (
                <Text
                  type={TextType.Secondary}
                  tagName={TagName.Paragraph}
                  data-hook={SingleSlotDetailsDataHook.PARTICIPANT_NAME}
                >
                  {name}
                </Text>
              )}
            </div>
            <SelectedAddons
              slotService={slotService}
              selectedAddons={participant.selectedAddons}
              dataHook={`${SingleSlotDetailsDataHook.PARTICIPANT_ADDONS}-${index}`}
            />
          </div>
        );
      })}
    </div>
  );
};

export default ParticipantsDetails;
